import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

function publicClient() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

// Map DB row -> JSON-compatible shape consumed by /benzo
function mapBenzoRow(r: any) {
  return {
    Substance: r.substance,
    Class: r.compound_class ?? "",
    "Equiv. to Diazepam 10mg": r.equiv_dose ?? "",
    "Typical Dose (mg)": r.typical_dose ?? "",
    "Max Daily (mg)": r.max_daily ?? "",
    Anxiolytic: r.anxiolytic ?? 0,
    "Sedative/ Hypnotic": r.sedative_hypnotic ?? 0,
    "Muscle Relaxant": r.muscle_relaxant ?? 0,
    Anticonvulsant: r.anticonvulsant ?? 0,
    "Dependence Risk": r.dependence_risk ?? 0,
    Notes: r.notes ?? "",
    Notes_de: r.notes_de ?? "",
    pharma: (r.pharma ?? {}) as Record<string, string>,
    warning: !!r.warning,
    nonComparable: !!r.non_comparable,
  };
}

function mapOpioidRow(r: any) {
  return {
    Substance: r.substance,
    Class: r.compound_class ?? "",
    "Equiv. to Morphin 10mg": r.equiv_dose ?? "",
    "Typical Dose (mg)": r.typical_dose ?? "",
    "Max Daily (mg)": r.max_daily ?? "",
    Analgesic: r.analgesic ?? 0,
    Sedative: r.sedative ?? 0,
    "Respiratory Depression": r.respiratory_depression ?? 0,
    Euphoria: r.euphoria ?? 0,
    "Dependence Risk": r.dependence_risk ?? 0,
    Notes: r.notes ?? "",
    Notes_de: r.notes_de ?? "",
    pharma: (r.pharma ?? {}) as Record<string, string>,
    warning: !!r.warning,
    nonComparable: !!r.non_comparable,
  };
}

export const listBenzosPublic = createServerFn({ method: "GET" }).handler(async () => {
  const sb = publicClient();
  const { data, error } = await sb
    .from("benzo_compounds")
    .select("substance, compound_class, equiv_dose, typical_dose, max_daily, anxiolytic, sedative_hypnotic, muscle_relaxant, anticonvulsant, dependence_risk, notes, notes_de, pharma, warning, non_comparable, sort_order")
    .eq("published", true)
    .order("sort_order", { ascending: true })
    .order("substance", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []).map(mapBenzoRow);
});

export const listOpioidsPublic = createServerFn({ method: "GET" }).handler(async () => {
  const sb = publicClient();
  const { data, error } = await sb
    .from("opioid_compounds")
    .select("substance, compound_class, equiv_dose, typical_dose, max_daily, analgesic, sedative, respiratory_depression, euphoria, dependence_risk, notes, notes_de, pharma, warning, non_comparable, sort_order")
    .eq("published", true)
    .order("sort_order", { ascending: true })
    .order("substance", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []).map(mapOpioidRow);
});

function mapTerpeneRow(r: any) {
  return {
    name: r.name,
    synonym: r.synonym ?? null,
    terpeneClass: r.terpene_class ?? "",
    aroma: r.aroma ?? "",
    boilingPoint: r.boiling_point ?? "",
    boilingPointNote: r.boiling_point_note ?? null,
    colorHex: r.color_hex ?? "",
    colorDerived: !!r.color_derived,
    effect: r.effect ?? "",
    evidence: (r.evidence ?? "") as "etabliert" | "präklinisch" | "begrenzt" | "",
    warningNote: r.warning_note ?? null,
    mergeNote: r.merge_note ?? null,
  };
}

export const listTerpenesPublic = createServerFn({ method: "GET" }).handler(async () => {
  const sb = publicClient();
  const { data, error } = await sb
    .from("terpene_compounds")
    .select("name, synonym, terpene_class, aroma, boiling_point, boiling_point_note, color_hex, color_derived, effect, evidence, warning_note, merge_note, sort_order")
    .eq("published", true)
    .order("sort_order", { ascending: true })
    .order("name", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []).map(mapTerpeneRow);
});

